import { StateCreator, create } from 'zustand';
import { BrowserMe } from '@bloomays-lib/types.shared';
import { ME } from '@bloomays-lib/adapter.api-bloomer';
import { Logger } from '../services/serviceLogger';
import { ApolloClient } from '@apollo/client';
import { getApolloErrorCode } from '../helpers/error';
import { isAuth0TokenRefreshError } from '@bloomays-lib/common.auth/browser';
const logger = Logger('userStore');

export interface UserState {
  apolloClient: ApolloClient<any> | null;
  setApolloClient: (client: ApolloClient<any>) => void;
  user?: BrowserMe | null;
  userLoading: boolean;
  userError?: Error;
  userInit: () => void;
  userFetch: (reason?: string) => Promise<void>;
  clearUserError: () => void;
}

export const createUserSlice: StateCreator<UserState> = (set, get) => {
  const handleUserError = (error: any, reason?: string) => {
    const errorCode = getApolloErrorCode(error);

    // Handle authentication-related errors gracefully
    if (errorCode === 'UNKNOWN_BLOOMER' || errorCode === 'UNAUTHENTICATED' || errorCode === 'TOKEN_EXPIRED') {
      // These are expected authentication errors that should not bubble up
      logger.debug(`Authentication error handled gracefully: ${errorCode}`, { reason });
      set({ user: null, userLoading: false });
      return;
    }

    // Handle Auth0 token refresh errors from GraphQL queries
    // These can occur when apolloClient.query() triggers getAccessTokenSilently()
    if (isAuth0TokenRefreshError(error)) {
      logger.debug('Auth0 token error in GraphQL query, creating managed error for redirect', { reason });
      // Create a specific error that UserProvider can detect and handle
      const managedError = new Error('Auth0 session expired during user fetch', { cause: error });
      (managedError as any).isAuth0SessionExpired = true;
      set({ userError: managedError, userLoading: false });
      return;
    }

    // For unexpected errors, create managed error but don't throw immediately
    const managedError = new Error('Impossible de charger le profil utilisateur', { cause: error });
    set({ userError: managedError, userLoading: false });
    logger.debug('fetch user ❌', { reason });
    throw error;
  };

  return {
    apolloClient: null,
    setApolloClient: (client) => set({ apolloClient: client }),
    user: undefined,
    userLoading: false,
    userError: undefined,
    userInit: () => {
      set({ user: undefined, userLoading: false, userError: undefined });
      logger.debug('init user ✅');
    },
    clearUserError: () => {
      set({ userError: undefined });
      logger.debug('user error cleared');
    },
    userFetch: async (reason) => {
      logger.debug('fetch user 👀', { reason });
      const { apolloClient } = get();
      if (!apolloClient) {
        throw new Error('Apollo Client Not Initialized');
      }
      set({ userLoading: true });
      try {
        apolloClient.watchQuery({ query: ME }).subscribe({
          next: ({ data }) => {
            set({ user: data?.me, userLoading: false });
            logger.debug('refetch user ✅', { reason });
          },
          error: (error) => {
            return handleUserError(error, reason);
          },
        });
        await apolloClient.query<{ me: BrowserMe }>({ query: ME });
      } catch (error: any) {
        return handleUserError(error, reason);
      }
    },
  };
};

export const useUserStore = create<UserState>()((...a) => ({
  ...createUserSlice(...a),
}));
